///<reference types="pixi.js" />

namespace pixi_ui {
    import Container = PIXI.Container;
    import Point = PIXI.Point;
    import IComponent = pixi_ui.component.IComponent;
    import ObservablePoint = PIXI.ObservablePoint;

    export enum ElementType {
        'group',
        'image',
        'nineslice',
        'text',
        'button',
        'json',
        'rect',
    }

    export class Element extends Container {
        public type: ElementType;
        public components: IComponent[];
        public offsetTopLeft: ObservablePoint;
        public offsetBottomRight: ObservablePoint;
        public anchorTopLeft: ObservablePoint;
        public anchorBottomRight: ObservablePoint;

        /**
         * Custom pivot based on elementWidth and elementHeight e.g. (0.5,0.5) is 'center' of Element.
         */
        public elementPivot: ObservablePoint;

        private _elementWidth: number;
        private _elementHeight: number;

        public desiredWidth: number;
        public desiredHeight: number;

        public parentWidth: number;
        public parentHeight: number;

        public contentWidth: number;
        public contentHeight: number;

        public widthMax: number;
        public widthMin: number;

        public heightMax: number;
        public heightMin: number;

        public updates: ((delta: number) => void)[];

        constructor() {
            super();
            this.components = [];
            this.anchorTopLeft = new ObservablePoint(
                this.setPosition,
                this,
                0.5,
                0.5
            );
            this.anchorBottomRight = new ObservablePoint(
                this.setPosition,
                this,
                0.5,
                0.5
            );
            this.offsetTopLeft = new ObservablePoint(this.setPosition, this);
            this.offsetBottomRight = new ObservablePoint(
                this.setPosition,
                this
            );
            this.elementPivot = new ObservablePoint(
                this.setPivot,
                this,
                0.5,
                0.5
            );

            this.elementWidth = 100;
            this.elementHeight = 100;

            this.contentWidth = 1;
            this.contentHeight = 1;

            this.updates = [];
        }

        get elementWidth(): number {
            return this._elementWidth;
        }

        set elementWidth(value: number) {
            this._elementWidth = value;
            this.setPivot();
        }

        get elementHeight(): number {
            return this._elementHeight;
        }

        set elementHeight(value: number) {
            this._elementHeight = value;
            this.setPivot();
        }

        public setPivot() {
            this.pivot.set(
                this.elementWidth * this.elementPivot.x,
                this.elementHeight * this.elementPivot.y
            );
        }

        public setPosition() {
            this.position = this.getPosition();
        }

        public getAnchorCenter(): Point {
            return new Point(
                ((this.anchorTopLeft.x + this.anchorBottomRight.x) / 2) *
                    this.parentWidth,
                ((this.anchorTopLeft.y + this.anchorBottomRight.y) / 2) *
                    this.parentHeight
            );
        }

        public getSize(): Point {
            return new Point(
                this.elementWidth +
                    this.offsetTopLeft.x -
                    this.offsetBottomRight.x,
                this.elementHeight +
                    this.offsetTopLeft.y -
                    this.offsetBottomRight.y
            );
        }

        public getPosition(): Point {
            const anchorCenter: Point = this.getAnchorCenter();

            return new Point(
                anchorCenter.x - this.offsetTopLeft.x,
                anchorCenter.y - this.offsetTopLeft.y
            );
        }

        public add(component: IComponent) {
            component.attach(this);
            this.components.push(component);
        }

        public remove(component: IComponent) {
            let index: number = this.components.indexOf(component);
            if (index !== -1) {
                this.components.splice(index, 1);
                component.detach(this);
            }
        }

        public resize(width: number, height: number): void {
            this.elementWidth =
                (this.desiredWidth || width) +
                this.offsetTopLeft.x -
                this.offsetBottomRight.x;
            this.elementHeight =
                (this.desiredHeight || height) +
                this.offsetTopLeft.y -
                this.offsetBottomRight.y;

            // Size contraints.
            if (this.widthMin)
                this.elementWidth = Math.max(this.elementWidth, this.widthMin);
            if (this.widthMax)
                this.elementWidth = Math.min(this.elementWidth, this.widthMax);
            if (this.heightMin)
                this.elementHeight = Math.max(
                    this.elementHeight,
                    this.heightMin
                );
            if (this.heightMax)
                this.elementHeight = Math.min(
                    this.elementHeight,
                    this.heightMax
                );

            // Relative positioning.
            this.position = this.getPosition();

            if (this.parent && this.parent instanceof Element) {
                this.parentWidth = this.parent.elementWidth;
                this.parentHeight = this.parent.elementHeight;
            } else {
                this.parentWidth = width;
                this.parentHeight = height;
            }

            // Calculate content size.
            this.contentWidth = this.elementWidth;
            this.contentHeight = this.elementHeight;

            this.children.forEach(child => {
                if (child instanceof Element) {
                    child.resize(this.elementWidth, this.elementHeight);

                    const px: number = child.x | 0;
                    this.contentWidth = Math.max(
                        this.contentWidth,
                        px + child.contentWidth * (1 - child.elementPivot.x)
                    );

                    const py: number = child.y | 0;
                    this.contentHeight = Math.max(
                        this.contentHeight,
                        py + child.contentHeight * (1 - child.elementPivot.y)
                    );
                }
            });

            this.components.forEach(component => component.resize(this));
        }

        public update(delta: number) {
            this.children.forEach(c => {
                if (c instanceof Element) c.update(delta);
            });
            this.updates.forEach(u => u(delta));
        }
    }
}
